import React from 'react';
import {
  Box,
  Card,
  CardMedia,
  CardContent,
  Typography,
  Chip
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import THEME from '../../constants/theme';

/**
 * BlogCard Component
 * Reusable card component for blog listing page
 * Displays: cover image, category chips, read time, title, excerpt, date
 * Features: hover effects with transform and shadow enhancement
 *
 * @param {Object} blog - Blog object with id, title, coverImage, categories, readTime, excerpt, date
 * @returns {React.Component} - Formatted blog card
 */
const BlogCard = ({ blog }) => {
  const { id, title, coverImage, categories, readTime, excerpt, date } = blog;

  // Format date
  const formattedDate = new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <Card
      component={RouterLink}
      to={`/blog/${id}`}
      sx={{
        height: '100%',
        minHeight: '580px',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: THEME.COLORS.NEUTRAL_WHITE,
        border: `1px solid rgba(30, 82, 243, 0.15)`,
        borderRadius: THEME.BORDERS.RADIUS.LARGE,
        transition: `all ${THEME.TRANSITIONS.MEDIUM}`,
        cursor: 'pointer',
        textDecoration: 'none !important',
        overflow: 'hidden',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        '& *': {
          textDecoration: 'none !important'
        },
        '&:hover': {
          transform: 'translateY(-8px)',
          boxShadow: `0 12px 32px rgba(30, 82, 243, 0.15)`,
          borderColor: THEME.COLORS.PRIMARY_BLUE,
          '& h5': {
            textDecoration: 'underline !important'
          }
        }
      }}
    >
      {/* Cover Image */}
      <CardMedia
        component='img'
        height='300'
        image={coverImage}
        alt={title}
        sx={{
          objectFit: 'fill',
          transition: `transform ${THEME.TRANSITIONS.MEDIUM}`,
          '[data-link="true"] &:hover': {
            transform: 'scale(1.05)'
          }
        }}
      />

      <CardContent
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          padding: { xs: 2, sm: 2.5 }
        }}
      >
        {/* Category Chips */}
        <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap' }}>
          {categories.map((cat, index) => (
            <Chip
              key={index}
              label={cat}
              size='small'
              sx={{
                backgroundColor:
                  cat === 'PDF'
                    ? 'rgba(220, 53, 69, 0.1)'
                    : 'rgba(30, 82, 243, 0.1)',
                color: cat === 'PDF' ? '#DC3545' : THEME.COLORS.PRIMARY_BLUE,
                fontWeight: 600,
                fontSize: '0.75rem',
                height: 'auto',
                padding: '4px 8px',
                border: `1px solid ${cat === 'PDF' ? 'rgba(220, 53, 69, 0.3)' : 'rgba(30, 82, 243, 0.3)'}`,
                '& .MuiChip-label': {
                  padding: '0'
                }
              }}
            />
          ))}
        </Box>

        {/* Title */}
        <Typography
          variant='h5'
          sx={{
            fontSize: { xs: '1.1rem', sm: '1.25rem' },
            fontWeight: 700,
            lineHeight: 1.3,
            color: THEME.COLORS.NEUTRAL_BLACK,
            fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
            transition: `all ${THEME.TRANSITIONS.MEDIUM}`,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textDecoration: 'none'
          }}
        >
          {title}
        </Typography>

        {/* Excerpt */}
        <Typography
          variant='body2'
          sx={{
            color: THEME.COLORS.NEUTRAL_BLACK_LIGHT,
            fontSize: { xs: '0.9rem', sm: '0.95rem' },
            lineHeight: 1.5,
            fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            flexGrow: 1
          }}
        >
          {excerpt}
        </Typography>

        {/* Metadata */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1,
            paddingTop: 1.5,
            borderTop: `1px solid rgba(30, 82, 243, 0.1)`,
            fontSize: { xs: '0.8rem', sm: '0.85rem' },
            color: THEME.COLORS.NEUTRAL_BLACK_LIGHT
          }}
        >
          <span>{formattedDate}</span>
          <span>{readTime} min read</span>
        </Box>
      </CardContent>
    </Card>
  );
};

export default BlogCard;
